/**
 * Per-portal auth session storage.
 *
 * The admin and client portals run on the same origin, so they must NOT share
 * localStorage keys — logging into one would otherwise overwrite the other's
 * session, and reloading the other portal (or clearing cache) would bounce you
 * to the wrong login. We namespace the token + user by portal, derived from
 * the URL path (`/admin/*` → admin, everything else → client). The API layer
 * reads the matching token the same way, so each portal's requests carry its
 * own credentials and the two sessions coexist independently.
 */

function currentPortal() {
  if (typeof window === "undefined") return "client";
  return window.location.pathname.startsWith("/admin") ? "admin" : "client";
}

const tokenKey = (portal = currentPortal()) => `${portal}_token`;
const userKey  = (portal = currentPortal()) => `${portal}_user`;

// One-time migration: adopt a legacy un-namespaced session (the old `token` /
// `user` keys, used before sessions were split per portal) into the namespace
// matching its own role, so anyone signed in at upgrade time isn't bumped to
// login. Idempotent and best-effort.
(function migrateLegacySession() {
  if (typeof window === "undefined") return;
  try {
    const legacyUser  = localStorage.getItem("user");
    const legacyToken = localStorage.getItem("token");
    if (legacyUser && legacyToken) {
      const role = JSON.parse(legacyUser)?.role;
      const portal = role === "admin" ? "admin" : "client";
      if (!localStorage.getItem(tokenKey(portal))) {
        localStorage.setItem(tokenKey(portal), legacyToken);
        localStorage.setItem(userKey(portal), legacyUser);
      }
    }
  } catch { /* best-effort */ }
  finally {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }
})();

export function getToken(portal) {
  return localStorage.getItem(tokenKey(portal));
}

export function getStoredUser(portal) {
  const s = localStorage.getItem(userKey(portal));
  try { return s ? JSON.parse(s) : null; } catch { return null; }
}

export function saveSession(userData, token, portal) {
  localStorage.setItem(tokenKey(portal), token);
  localStorage.setItem(userKey(portal), JSON.stringify(userData));
}

export function clearSession(portal) {
  localStorage.removeItem(tokenKey(portal));
  localStorage.removeItem(userKey(portal));
}
